import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import type { BlogPost, Documentation } from "@shared/schema";

function toDate(val: any): Date | string | null {
  if (!val) return null;
  if (val instanceof Timestamp) return val.toDate();
  return val;
}

function docToBlogPost(id: string, data: any): BlogPost {
  return {
    id,
    title: data.title,
    slug: data.slug,
    excerpt: data.excerpt ?? null,
    content: data.content,
    category: data.category ?? "General",
    coverImageUrl: data.coverImageUrl ?? null,
    published: data.published ?? false,
    authorName: data.authorName ?? "DailyHisaab Team",
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

function docToDoc(id: string, data: any): Documentation {
  return {
    type: id,
    title: data.title,
    content: data.content ?? "",
    updatedAt: toDate(data.updatedAt),
  };
}

// ── Blog Posts ────────────────────────────────────────────────────────────────

export async function getBlogPosts(publishedOnly = true): Promise<BlogPost[]> {
  const colRef = collection(db, "blog_posts");
  const q = publishedOnly
    ? query(colRef, where("published", "==", true), orderBy("createdAt", "desc"))
    : query(colRef, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToBlogPost(d.id, d.data()));
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const colRef = collection(db, "blog_posts");
  const q = query(colRef, where("slug", "==", slug), where("published", "==", true));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return docToBlogPost(d.id, d.data());
}

export async function createBlogPost(
  data: Omit<BlogPost, "id" | "createdAt" | "updatedAt">
): Promise<BlogPost> {
  const ref = await addDoc(collection(db, "blog_posts"), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docToBlogPost(ref.id, { ...data, createdAt: new Date(), updatedAt: new Date() });
}

export async function updateBlogPost(
  id: string,
  data: Partial<Omit<BlogPost, "id">>
): Promise<void> {
  const ref = doc(db, "blog_posts", id);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
}

export async function deleteBlogPost(id: string): Promise<void> {
  await deleteDoc(doc(db, "blog_posts", id));
}

export async function importBlogPost(
  data: Omit<BlogPost, "id"> & { id?: string }
): Promise<BlogPost> {
  const ref = await addDoc(collection(db, "blog_posts"), {
    title: data.title,
    slug: data.slug,
    excerpt: data.excerpt ?? null,
    content: data.content,
    category: data.category ?? "General",
    coverImageUrl: data.coverImageUrl ?? null,
    published: data.published ?? false,
    authorName: data.authorName ?? "DailyHisaab Team",
    createdAt: data.createdAt ? new Date(data.createdAt as any) : serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docToBlogPost(ref.id, data);
}

// ── Documentation ─────────────────────────────────────────────────────────────

export async function getDocumentation(type: string): Promise<Documentation | null> {
  const ref = doc(db, "documentation", type);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return docToDoc(type, snap.data());
}

export async function upsertDocumentation(
  type: string,
  data: { title: string; content: string }
): Promise<Documentation> {
  const ref = doc(db, "documentation", type);
  await setDoc(ref, { ...data, type, updatedAt: serverTimestamp() }, { merge: true });
  return { type, ...data, updatedAt: new Date() };
}

export async function importDocumentation(
  type: string,
  data: { title: string; content: string; updatedAt?: any }
): Promise<void> {
  const ref = doc(db, "documentation", type);
  await setDoc(ref, {
    type,
    title: data.title,
    content: data.content ?? "",
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : serverTimestamp(),
  });
}
